import { useState, useEffect, useCallback } from "react";
import { api, errMsg } from "../../lib/api";
import StatusBadge from "../../components/StatusBadge";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Checkbox } from "../../components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { toast } from "sonner";

export default function AdminApprovals({ onChanged }) {
  const [subs, setSubs] = useState([]);
  const [status, setStatus] = useState("pending");
  const [selected, setSelected] = useState([]);
  const [notes, setNotes] = useState({});
  const [preview, setPreview] = useState(null);
  const [confirmRevoke, setConfirmRevoke] = useState(null);
  const [q, setQ] = useState("");

  const load = useCallback(() => {
    api.get("/admin/submissions", { params: { status } }).then(({ data }) => { setSubs(data); setSelected([]); });
  }, [status]);
  useEffect(() => { load(); }, [load]);

  const review = async (id, st) => {
    try {
      await api.put(`/admin/submissions/${id}`, { status: st, note: notes[id] || null });
      toast.success(st === "approved" ? "Approved" : "Rejected");
      load(); onChanged?.();
    } catch (err) { toast.error(errMsg(err)); }
  };

  const bulk = async () => {
    try {
      const { data } = await api.post("/admin/submissions/bulk-approve", { ids: selected });
      toast.success(`Approved ${data.approved} submissions`);
      load(); onChanged?.();
    } catch (err) { toast.error(errMsg(err)); }
  };

  const revoke = async () => {
    try {
      await api.post(`/admin/submissions/${confirmRevoke.id}/revoke`);
      toast.success("Revoked — points removed and the entry is off the Feed");
      setConfirmRevoke(null);
      load(); onChanged?.();
    } catch (err) { toast.error(errMsg(err)); }
  };

  const shown = subs.filter((s) => !q || s.property_name.toLowerCase().includes(q.toLowerCase()));

  return (
    <div>
      <div className="flex flex-wrap items-center gap-2 mb-3">
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger className="w-36" data-testid="queue-status-filter"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="pending">Pending</SelectItem><SelectItem value="approved">Approved</SelectItem><SelectItem value="rejected">Rejected</SelectItem><SelectItem value="all">All</SelectItem></SelectContent>
        </Select>
        <Input placeholder="Search property…" value={q} onChange={(e) => setQ(e.target.value)} className="max-w-56" data-testid="queue-property-search" />
        {status === "pending" && selected.length > 0 && (
          <Button size="sm" onClick={bulk} className="bg-green-600 hover:bg-green-700 text-white" data-testid="bulk-approve-btn">Approve {selected.length} selected</Button>
        )}
        <span className="text-sm text-slate-500 ml-auto" data-testid="queue-count">{shown.length} submissions</span>
      </div>
      <div className="space-y-2">
        {shown.length === 0 && <p className="text-sm text-slate-500 py-10 text-center border border-dashed border-slate-300 rounded-md">Queue is clear.</p>}
        {shown.map((s) => (
          <div key={s.id} className="border border-slate-200 rounded-md p-3 flex flex-wrap items-center gap-3" data-testid={`queue-row-${s.id}`}>
            {s.status === "pending" && (
              <Checkbox checked={selected.includes(s.id)} onCheckedChange={(c) => setSelected(c ? [...selected, s.id] : selected.filter((x) => x !== s.id))} data-testid={`queue-check-${s.id}`} />
            )}
            {s.photo && <img src={s.photo} alt="" className="w-16 h-16 rounded object-cover cursor-pointer hover:opacity-80" onClick={() => setPreview(s.photo)} data-testid={`queue-photo-${s.id}`} />}
            <div className="flex-1 min-w-48">
              <div className="font-medium text-sm text-[#1a1f36]">{s.task_name} <span className="text-[#c8102e] font-bold">+{s.points}</span></div>
              <div className="text-xs text-slate-500">{s.property_name} · {s.person_name} ({s.person_role}){s.person_role === "support" && !s.visited_property_name ? <span className="ml-1 px-1.5 py-0.5 bg-blue-50 text-blue-700 rounded-full font-semibold" data-testid={`queue-support-badge-${s.id}`}>with Support Team</span> : null}{s.visited_property_name ? ` · visited ${s.visited_property_name} (+10 to them on approval)` : ""} · sign-off: {s.supervisor}</div>
              <div className="text-xs text-slate-400">{new Date(s.created_at).toLocaleString()}</div>
              {s.activity?.length > 0 && (
                <div className="text-[11px] text-slate-400 mt-1" data-testid={`queue-activity-${s.id}`}>
                  {s.activity.map((a) => (
                    <div key={`${a.at}-${a.action}`}>
                      <span className={`font-semibold uppercase tracking-wide ${a.action === "approved" ? "text-green-600" : a.action === "revoked" ? "text-red-500" : "text-slate-500"}`}>{a.action}</span>
                      {" "}by {a.by || "Support Team"} · {new Date(a.at).toLocaleString()}{a.note ? ` — “${a.note}”` : ""}
                    </div>
                  ))}
                </div>
              )}
            </div>
            <StatusBadge status={s.status} />
            {s.status === "approved" && (
              <Button size="sm" variant="outline" className="text-red-600 border-red-200 hover:bg-red-50" onClick={() => setConfirmRevoke(s)} data-testid={`queue-revoke-${s.id}`}>Revoke</Button>
            )}
            {s.status === "pending" && (
              <div className="flex items-center gap-2">
                <Input placeholder="Note (optional)" className="w-40 h-8 text-xs" value={notes[s.id] || ""} onChange={(e) => setNotes({ ...notes, [s.id]: e.target.value })} data-testid={`queue-note-${s.id}`} />
                <Button size="sm" className="bg-green-600 hover:bg-green-700 text-white" onClick={() => review(s.id, "approved")} data-testid={`queue-approve-${s.id}`}>Approve</Button>
                <Button size="sm" variant="outline" className="text-red-600 border-red-200 hover:bg-red-50" onClick={() => review(s.id, "rejected")} data-testid={`queue-reject-${s.id}`}>Reject</Button>
              </div>
            )}
          </div>
        ))}
      </div>
      {confirmRevoke && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-6" data-testid="revoke-confirm-dialog">
          <div className="bg-white rounded-md p-5 max-w-sm w-full shadow-xl">
            <div className="font-display font-semibold text-[#1a1f36] mb-2">Revoke this submission?</div>
            <p className="text-sm text-slate-600 mb-1">Points are removed and it disappears from the Feed.</p>
            <p className="text-xs text-slate-500 mb-4">{confirmRevoke.task_name} · {confirmRevoke.property_name} · {confirmRevoke.person_name} (+{confirmRevoke.points}){confirmRevoke.visited_property_name ? ` — also removes the +10 visit bonus from ${confirmRevoke.visited_property_name}` : ""}</p>
            <div className="flex justify-end gap-2">
              <Button size="sm" variant="outline" onClick={() => setConfirmRevoke(null)} data-testid="revoke-cancel-btn">Cancel</Button>
              <Button size="sm" className="bg-red-600 hover:bg-red-700 text-white" onClick={revoke} data-testid="revoke-confirm-btn">Revoke</Button>
            </div>
          </div>
        </div>
      )}
      {preview && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-6" onClick={() => setPreview(null)} data-testid="photo-preview-overlay">
          <img src={preview} alt="submission" className="max-h-full max-w-full rounded-md" />
        </div>
      )}
    </div>
  );
}
